import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Loader2, Shield } from 'lucide-react';
import { getSkillRisk } from '@kirin/api-client/api/skills';
import { mcpRiskLevelColors } from '@kirin/api-client/contracts/mcp';
import type {
  SkillDataTableItem,
  SkillRiskCopy,
} from '@kirin/api-client/contracts/skills';
import type { OrganizationId } from '@kirin/api-client/contracts/organizations';
import { McpRiskBadge } from '@/components/shared/mcp/McpRiskBadge';
import { queryKeys } from '@/hooks/api/queryKeys';
import { apiClient } from '@/lib/api-client';
import { formatRelativeTime } from '@/lib/date-utils';
import {
  SKILL_RISK_UNAVAILABLE_COPY,
  skillHashErrorText,
} from './skillRiskCopy';

interface SkillRiskPanelProps {
  organizationId: string;
  row: SkillDataTableItem;
  /** The group list the row is in (KIR-4701), or null for the organization. */
  securityGroupId?: string | null;
}

const userLabel = (n: number): string => `${n} ${n === 1 ? 'user' : 'users'}`;

const MAX_LOCAL_ONLY_SHOWN = 5;

function formatLocalOnly(paths: string[]): string {
  const shown = paths.slice(0, MAX_LOCAL_ONLY_SHOWN).join(', ');
  const more = paths.length - MAX_LOCAL_ONLY_SHOWN;
  return more > 0 ? `${shown} and ${more} more.` : `${shown}.`;
}

function CopyDetails({ copy }: { copy: SkillRiskCopy }): ReactElement {
  if (!copy.risk) {
    return (
      <div className="space-y-1 text-sm text-muted-foreground">
        <p>
          {
            SKILL_RISK_UNAVAILABLE_COPY[
              copy.riskUnavailableReason ?? 'not_reported'
            ]
          }
        </p>
        {copy.hashError && (
          <p className="text-xs">{skillHashErrorText(copy.hashError)}</p>
        )}
      </div>
    );
  }
  const { risk } = copy;
  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <McpRiskBadge level={risk.level} size="lg" />
        <p className="text-xs text-muted-foreground">
          Last synced {formatRelativeTime(risk.lastSyncedAt)}
          {copy.matchCount !== null && copy.matchCount > 1
            ? ` · worst of ${copy.matchCount} AgentMesh skills with these exact files`
            : ''}
        </p>
      </div>

      {copy.riskFactors.length > 0 && (
        <div className="space-y-1.5">
          <h5 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Top risk factors
          </h5>
          <ul className="space-y-1">
            {copy.riskFactors.map((factor, index) => (
              // Index in the key: AgentMesh may repeat a factor string.
              <li
                key={`${index}:${factor}`}
                className="flex items-start gap-2 text-sm"
              >
                <span
                  aria-hidden="true"
                  className="mt-1.5 size-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: mcpRiskLevelColors[risk.level] }}
                />
                <span className="break-words">{factor}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {copy.sources.length > 0 && (
        <div className="space-y-1.5">
          <h5 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Found in AgentMesh as
          </h5>
          <ul className="space-y-1">
            {copy.sources.map((source, index) => (
              <li
                key={`${index}:${source.gitRepo ?? source.name ?? ''}`}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <span
                  className="min-w-0 truncate"
                  title={source.gitRepo ?? undefined}
                >
                  {source.gitRepo ?? source.name ?? 'Unnamed skill'}
                </span>
                <McpRiskBadge level={source.level} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {copy.leavesOutLocalFiles && copy.localOnlyPaths.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Matched without files that exist only on users&apos; computers, which
          this result does not cover: {formatLocalOnly(copy.localOnlyPaths)}
        </p>
      )}

      {copy.excludedPaths.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Repository metadata (.git) in this skill&apos;s folder is not covered.
        </p>
      )}

      {copy.agentmeshUrl && (
        <a
          href={copy.agentmeshUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
        >
          <ExternalLink className="size-3.5" aria-hidden="true" />
          View full report in AgentMesh
        </a>
      )}
    </div>
  );
}

/**
 * "Risk (from AgentMesh)" in the expanded Skills row (KIR-5099): the verdict
 * AgentMesh gave the skill's exact files, why it flagged them, which
 * catalogued skills have the same files, and the full report. A skill whose
 * users have different files (another commit, a local edit) lists each copy,
 * worst first. Fetched when the row expands; AgentMesh owns the value.
 */
export function SkillRiskPanel({
  organizationId,
  row,
  securityGroupId = null,
}: SkillRiskPanelProps): ReactElement {
  const query = {
    name: row.name,
    source: row.source,
    ...(row.project ? { project: row.project } : {}),
    ...(row.version ? { versionHash: row.version.hash } : {}),
    ...(securityGroupId ? { securityGroupId } : {}),
  };
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.skills.risk(organizationId, query),
    queryFn: ({ signal }) =>
      getSkillRisk(apiClient, organizationId as OrganizationId, query, {
        signal,
      }),
    staleTime: 60 * 1000,
  });

  const copies = data?.copies ?? [];

  return (
    <section
      aria-label="Risk from AgentMesh"
      className="space-y-3 rounded-md border p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">Risk</h4>
        <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700 dark:bg-purple-950/40 dark:text-purple-300">
          <Shield className="size-3" aria-hidden="true" />
          From AgentMesh
        </span>
      </div>

      {isLoading ? (
        <span
          role="status"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground"
        >
          <Loader2 aria-hidden className="size-3.5 animate-spin" />
          Loading risk…
        </span>
      ) : isError ? (
        <p className="text-sm text-muted-foreground">
          Could not load the risk from AgentMesh.
        </p>
      ) : copies.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {
            SKILL_RISK_UNAVAILABLE_COPY[
              data?.riskUnavailableReason ?? 'not_reported'
            ]
          }
        </p>
      ) : copies.length === 1 ? (
        <CopyDetails copy={copies[0]} />
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            This skill&apos;s users have {copies.length} copies of its files
            that differ or could not be compared. Each is checked on its own;
            the worst is shown first.
          </p>
          <ol className="space-y-3">
            {copies.map((copy, index) => (
              <li
                key={copy.skillTreeSha256 ?? `unhashed-${index}`}
                className="space-y-2 border-t pt-3 first:border-t-0 first:pt-0"
              >
                <p className="text-xs font-medium">
                  Copy {index + 1} · {userLabel(copy.userCount)}
                </p>
                <CopyDetails copy={copy} />
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}

SkillRiskPanel.displayName = 'SkillRiskPanel';
